/**
 * useAndonAlerts.ts
 * Polling hook para alertas de SLA Andon.
 *
 * Visibilidade delegada ao RLS do Supabase — o front-end apenas
 * filtra os status relevantes por role para montar o badge.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import type { RoleType } from '../services/scopeService';

export type SlaStatus = 'andon_triggered' | 'escalated_manager' | 'critical_director';

export interface AndonAlert {
  id: string;
  title: string;
  sla_status: SlaStatus;
  due_date: string | null;
  assigned_to: string | null;
}

export interface AndonAlertCounts {
  /** Alerts visíveis para o papel atual (role-filtered) */
  critical: number;
  escalated: number;
  andon: number;
  /** Total de alertas que o RLS retornou (sem role filter) — usado pelo badge do header */
  total: number;
  /** Total bruto do banco (igual a total para header badge) */
  raw: number;
}

export interface UseAndonAlertsResult {
  alerts: AndonAlert[];
  counts: AndonAlertCounts;
  isLoading: boolean;
  refetch: () => void;
}

const POLL_INTERVAL_MS = 60_000;

const ALL_BREACH_STATUSES: SlaStatus[] = [
  'critical_director',
  'escalated_manager',
  'andon_triggered',
];

export function useAndonAlerts(
  organizationId: string | null | undefined,
  _userId: string | null | undefined,
  roleType: RoleType = 'operational',
): UseAndonAlertsResult {
  const { data, isLoading, refetch } = useQuery({
    // Inclui roleType no key para que mudança de papel invalide o cache
    queryKey: ['andon-alerts', organizationId, roleType],
    enabled: Boolean(organizationId),
    meta: { suppressErrors: true },
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    // staleTime:0 = sempre busca dados frescos; gcTime padrão (5min) = mantém
    // dado anterior visível enquanto refetch acontece (sem flicker de null)
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('actions')
        .select('id, title, sla_status, due_date, assigned_to')
        .eq('organization_id', organizationId!)
        .in('sla_status', ALL_BREACH_STATUSES)
        // PostgREST NOT IN: sem aspas internas
        .not('status', 'in', '(completed,cancelled)')
        .order('sla_status', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data ?? []) as AndonAlert[];
    },
  });

  // allAlerts = tudo que o RLS devolveu (sem filtro de role client-side extra)
  const allAlerts = data ?? [];

  // Filtragem client-side por role — usada apenas no dropdown do AndonBadge
  const alerts = allAlerts.filter(a => {
    if (roleType === 'director' || roleType === 'lead') return true;
    if (roleType === 'manager') return a.sla_status !== 'critical_director';
    // operational: apenas andon_triggered visíveis por RLS
    return a.sla_status === 'andon_triggered';
  });

  const counts: AndonAlertCounts = {
    critical: allAlerts.filter(a => a.sla_status === 'critical_director').length,
    escalated: allAlerts.filter(a => a.sla_status === 'escalated_manager').length,
    andon: allAlerts.filter(a => a.sla_status === 'andon_triggered').length,
    // total e raw usam allAlerts para o badge do header nunca suprimir alertas reais
    total: allAlerts.length,
    raw: allAlerts.length,
  };

  return { alerts, counts, isLoading, refetch };
}
